import React from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Componente que exibe o resumo do agendamento
 * 
 * @param {Object} props - Propriedades do componente
 * @param {Array} props.selectedServices - Serviços selecionados
 * @param {Date} props.selectedDate - Data selecionada
 * @param {string} props.selectedTime - Horário selecionado
 * @param {Object} props.selectedProfessional - Profissional selecionado
 * @param {boolean} props.minimized - Se deve exibir em modo resumido
 */
const BookingSummary = ({
  selectedServices = [],
  selectedDate,
  selectedTime,
  selectedProfessional,
  minimized = false
}) => {
  // Cálculo do tempo total
  const totalDuration = selectedServices.reduce((total, service) => 
    total + (typeof service === 'object' ? service.duracao : 0), 0);
  
  // Cálculo do preço total (considerando preços customizados do profissional)
  const totalPrice = selectedServices.reduce((total, service) => {
    if (typeof service !== 'object') return total;
    
    // Verificar se existe preço personalizado para o profissional
    if (selectedProfessional && selectedProfessional.id && 
        service.professionalCustomizations && 
        service.professionalCustomizations[selectedProfessional.id] &&
        service.professionalCustomizations[selectedProfessional.id].customPrice !== null) {
      return total + service.professionalCustomizations[selectedProfessional.id].customPrice;
    }
    
    return total + service.preco;
  }, 0);
  
  // Formatar a data
  const formattedDate = selectedDate 
    ? format(selectedDate, "EEEE, d 'de' MMMM", { locale: ptBR }) 
    : '';
  
  // Versão minizada para mobile ou widgets
  if (minimized) {
    return (
      <div className="p-3 bg-gray-50 rounded-lg">
        <div className="flex justify-between">
          <div>
            <p className="text-sm text-gray-600">{selectedServices.length} serviço(s)</p>
            {selectedDate && selectedTime && (
              <p className="text-sm font-medium">{formattedDate}, {selectedTime}</p>
            )}
          </div>
          <p className="font-medium">R$ {totalPrice.toFixed(2)}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="border rounded-lg p-4 bg-gray-50">
      <h3 className="text-lg font-semibold mb-3">Resumo do Agendamento</h3>
      
      <div className="space-y-4">
        {/* Serviços */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Serviços</h4>
          <ul className="space-y-2">
            {selectedServices.map(service => (
              <li key={service.id} className="flex justify-between text-sm">
                <span>{service.nome}</span>
                <span className="font-medium">
                  R$ {
                    selectedProfessional && 
                    service.professionalCustomizations && 
                    service.professionalCustomizations[selectedProfessional.id]?.customPrice !== undefined
                      ? service.professionalCustomizations[selectedProfessional.id].customPrice.toFixed(2)
                      : service.preco.toFixed(2)
                  }
                </span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Data e Hora */}
        {selectedDate && selectedTime && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Data e Hora</h4>
            <p className="text-sm">{formattedDate}</p>
            <p className="text-sm">{selectedTime}</p>
          </div>
        )}
        
        {/* Profissional */}
        {selectedProfessional && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Profissional</h4>
            <p className="text-sm">{selectedProfessional.nome}</p>
          </div>
        )}
        
        {/* Duração e Preço */}
        <div className="pt-3 border-t border-gray-200">
          <div className="flex justify-between text-sm">
            <span>Duração total:</span>
            <span>{totalDuration} min</span>
          </div>
          <div className="flex justify-between font-medium mt-2">
            <span>Valor total:</span>
            <span>R$ {totalPrice.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSummary; 